import ApiClient from '../ApiClient';
import SmtpConfigService from './SmtpConfigService';
import { getGmailConnectionStatus, disconnectGmail } from '../pages/Email/GmailAuthHelper';

export const GMAIL_SESSION_EXPIRED_MESSAGE =
  'Your Gmail session has expired or is missing send permission. Please reconnect Gmail under Email settings.';

const isGmailSessionExpiredError = (err) =>
  err?.message === GMAIL_SESSION_EXPIRED_MESSAGE
  || String(err?.message || '').includes('Gmail session has expired');

const EMAIL_AUTH_STORAGE_KEY = "email_auth";
const EMAIL_AUTH_PROVIDERS_STORAGE_KEY = "email_auth_providers";
const EMAIL_AUTH_ACTIVE_KEY = "email_auth_active";

const toEmailAuthPayload = (emailType, accessToken = '', fromEmail = '') => ({
  emailType: emailType || '',
  fromEmail: fromEmail || '',
  accessToken: accessToken || '',
});

const getActiveEmailProvider = () => {
  try {
    return JSON.parse(localStorage.getItem(EMAIL_AUTH_ACTIVE_KEY)) || null;
  } catch {
    return null;
  }
};

const persistActiveEmailProvider = (emailType, meta = {}) => {
  localStorage.setItem(
    EMAIL_AUTH_ACTIVE_KEY,
    JSON.stringify({ emailType, ...meta }),
  );
};

const buildAuthFromRecord = (record) => {
  if (!record) return null;

  const emailType = record.emailtype || record.emailType || '';
  const accessToken = record.access_token || record.accessToken || '';
  const fromEmail = record.email || record.fromEmail || '';

  if (!emailType && !accessToken && !fromEmail) return null;

  return toEmailAuthPayload(emailType, accessToken, fromEmail);
};

const buildSmtpAuthPayload = () => {
  try {
    const active = getActiveEmailProvider();
    if (active?.emailType === 'smtp' && active.fromEmail) {
      return toEmailAuthPayload('smtp', active.accessToken || '', active.fromEmail);
    }

    const smtpSessions = JSON.parse(localStorage.getItem('smtp_sessions') || '[]');
    if (Array.isArray(smtpSessions) && smtpSessions.length > 0) {
      const configs = SmtpConfigService.getConfigs();
      const session = smtpSessions[0];
      const config = configs.find((item) => item.id === session.configId);
      if (config?.senderEmail) {
        return toEmailAuthPayload('smtp', '', config.senderEmail);
      }
    }

    const fallbackConfig = SmtpConfigService.getConfigs().find((item) => item.senderEmail);
    if (fallbackConfig) {
      return toEmailAuthPayload('smtp', '', fallbackConfig.senderEmail);
    }
  } catch {
    // ignore parse errors
  }

  return null;
};

const resolveStoredEmailAuthPayload = () => {
  const active = getActiveEmailProvider();

  if (active?.emailType === 'smtp') {
    const smtpAuth = buildSmtpAuthPayload();
    if (smtpAuth) return smtpAuth;
  }

  const providerTokens = getStoredProviderTokens();
  const emailAuthRecord = (() => {
    try {
      return JSON.parse(localStorage.getItem(EMAIL_AUTH_STORAGE_KEY));
    } catch {
      return null;
    }
  })();

  if (active?.emailType && active.emailType !== 'smtp') {
    const activeProvider = providerTokens[active.emailType];
    const activeFromProvider = buildAuthFromRecord(activeProvider);
    const activeFromEmailAuth = emailAuthRecord?.emailtype === active.emailType
      || emailAuthRecord?.emailType === active.emailType
      ? buildAuthFromRecord(emailAuthRecord)
      : null;

    if (activeFromEmailAuth?.accessToken) return activeFromEmailAuth;
    if (activeFromProvider?.accessToken) {
      return toEmailAuthPayload(
        active.emailType,
        activeFromProvider.accessToken,
        active.fromEmail || activeFromProvider.fromEmail || '',
      );
    }
  }

  const fromEmailAuth = buildAuthFromRecord(emailAuthRecord);
  if (fromEmailAuth?.accessToken) return fromEmailAuth;

  for (const emailType of ['gmail', 'zoho']) {
    const providerAuth = buildAuthFromRecord(providerTokens[emailType]);
    if (providerAuth?.accessToken) {
      if (emailType === 'gmail' && emailAuthRecord?.email && !providerAuth.fromEmail) {
        providerAuth.fromEmail = emailAuthRecord.email;
      }
      return providerAuth;
    }
  }

  return buildSmtpAuthPayload();
};

const parseZohoSender = (fromAddress = '', sender = '') => {
  // sender may be "Name <email>" or just a name; fromAddress is the raw email
  const match = sender.match(/^"?([^"<]+?)"?\s*<([^>]+)>$/);
  if (match) return { name: match[1].trim(), email: match[2].trim() };
  return { name: sender || fromAddress, email: fromAddress };
};

const IMAGE_EXTENSIONS = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp', svg: 'image/svg+xml' };

const guessMimeTypeFromName = (name = '') => {
  const ext = name.split('.').pop()?.toLowerCase();
  return IMAGE_EXTENSIONS[ext] || 'application/octet-stream';
};

const triggerBrowserDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'attachment';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const decodeBase64Url = (str) => {
  try {
    const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return '';
  }
};

const extractBody = (payload) => {
  if (!payload) return { html: '', plain: '' };
  if (payload.body?.data) {
    const decoded = decodeBase64Url(payload.body.data);
    if (payload.mimeType === 'text/html') return { html: decoded, plain: '' };
    return { html: '', plain: decoded };
  }
  if (payload.parts?.length) {
    let html = '', plain = '';
    for (const part of payload.parts) {
      const r = extractBody(part);
      if (r.html) html = r.html;
      if (r.plain) plain = r.plain;
    }
    return { html, plain };
  }
  return { html: '', plain: '' };
};

const getPartHeader = (part, name) =>
  (part.headers || []).find((h) => h.name?.toLowerCase() === name.toLowerCase())?.value || '';

const extractGmailAttachments = (payload) => {
  if (!payload) return [];
  const attachments = [];
  const walk = (part) => {
    if (!part) return;
    if (part.body?.attachmentId) {
      const contentIdRaw = getPartHeader(part, 'Content-ID');
      const contentId = contentIdRaw ? contentIdRaw.replace(/[<>]/g, '') : null;
      const isInline = getPartHeader(part, 'Content-Disposition').toLowerCase().startsWith('inline') || Boolean(contentId);
      if (part.filename || contentId) {
        attachments.push({
          attachmentId: part.body.attachmentId,
          name: part.filename || contentId || 'attachment',
          mimeType: part.mimeType || 'application/octet-stream',
          size: part.body.size || 0,
          contentId,
          isInline,
        });
      }
    }
    if (part.parts?.length) part.parts.forEach(walk);
  };
  walk(payload);
  return attachments;
};

const toStandardBase64 = (base64url) => {
  let base64 = (base64url || '').replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  return base64;
};

const base64ToBytes = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const parseFrom = (fromStr = '') => {
  const match = fromStr.match(/^"?([^"<]+?)"?\s*<([^>]+)>$/);
  if (match) return { name: match[1].trim(), email: match[2].trim() };
  return { name: fromStr, email: fromStr };
};

const getStoredProviderTokens = () => {
    try {
        return JSON.parse(localStorage.getItem(EMAIL_AUTH_PROVIDERS_STORAGE_KEY)) || {};
    } catch {
        return {};
    }
};

const setStoredProviderTokens = (tokensByProvider) => {
    localStorage.setItem(
        EMAIL_AUTH_PROVIDERS_STORAGE_KEY,
        JSON.stringify(tokensByProvider)
    );
};

const EmailService = {
  async sendGmail(to, subject, body, attachments = []) {
    const payload = { to, subject, body, isHtml: true, attachments };
    const resp = await ApiClient.post('/Email/send_gmail', payload);
    return resp;
  },

  async sendZoho(accessToken, to, subject, body, cc = [], bcc = [], isHtml = true, attachments = []) {
    if (!accessToken) throw new Error('Missing Zoho access token');
    
    const toArray = typeof to === 'string'
      ? to.split(',').map(email => email.trim()).filter(Boolean)
      : (Array.isArray(to) ? to : []);

    const ccArray = typeof cc === 'string'
      ? cc.split(',').map(email => email.trim()).filter(Boolean)
      : (Array.isArray(cc) ? cc : []);

    const bccArray = typeof bcc === 'string'
      ? bcc.split(',').map(email => email.trim()).filter(Boolean)
      : (Array.isArray(bcc) ? bcc : []);

    const payload = { 
      accessToken, 
      to: toArray, 
      cc: ccArray, 
      bcc: bccArray, 
      subject, 
      body, 
      isHtml, 
      attachments 
    };
    const resp = await ApiClient.post('/Email/send_zoho', payload);
    return resp;
  },

  async sendSmtp(toEmail, subject, body, attachments = []) {
    const payload = {
      ToEmail: toEmail,
      Subject: subject,
      Body: body,
      Attachments: attachments
    };
    const resp = await ApiClient.post('/Email/send_smtp', payload);
    return resp;
  },

  async getSmtpConfigurations() {
    return await ApiClient.get('/Email/smtp-configurations');
  },

  async getSmtpConfigurationById(id) {
    return await ApiClient.get(`/Email/smtp-configurations/${id}`);
  },

  async createSmtpConfiguration(config) {
    return await ApiClient.post('/Email/smtp-configurations', config);
  },

  async getGoogleUserInfo() {
    const resp = await ApiClient.get('/Email/gmail/userinfo');
    return resp;
  },

  async getMessages(labelId = 'INBOX', maxResults = 20) {
    const resp = await ApiClient.get('/Email/gmail/messages', {
      params: { labelId, maxResults },
    });
    return resp.data.messages || [];
  },

  async getDrafts(maxResults = 20) {
    const resp = await ApiClient.get('/Email/gmail/drafts', {
      params: { maxResults },
    });
    return resp.data.drafts || [];
  },

  async getMessageMeta(id) {
    const resp = await ApiClient.get(`/Email/gmail/messages/${id}/meta`);
    const msg = resp.data;
    const headers = msg.payload?.headers || [];
    const h = (name) => headers.find((x) => x.name.toLowerCase() === name.toLowerCase())?.value || '';
    const from = parseFrom(h('From'));
    return {
      id: msg.id,
      threadId: msg.threadId,
      subject: h('Subject') || '(no subject)',
      from,
      date: h('Date'),
      snippet: msg.snippet || '',
      isUnread: (msg.labelIds || []).includes('UNREAD'),
      labelIds: msg.labelIds || [],
    };
  },

  async getMessage(id) {
    const resp = await ApiClient.get(`/Email/gmail/messages/${id}`);
    const msg = resp.data;
    const headers = msg.payload?.headers || [];
    const h = (name) => headers.find((x) => x.name.toLowerCase() === name.toLowerCase())?.value || '';
    const from = parseFrom(h('From'));
    let { html, plain } = extractBody(msg.payload);
    const allAttachments = extractGmailAttachments(msg.payload);

    // Resolve inline (cid:) signature/embedded images directly into the HTML body
    const inlineResolvedIds = new Set();
    if (html) {
      for (const att of allAttachments) {
        if (!att.isInline || !att.contentId) continue;
        const cidRef = `cid:${att.contentId}`;
        if (!html.includes(cidRef)) continue;
        try {
          const dataUrl = await this.fetchGmailAttachmentDataUrl(id, att);
          html = html.split(cidRef).join(dataUrl);
          inlineResolvedIds.add(att.attachmentId);
        } catch {
          // leave the broken cid: reference if the fetch fails
        }
      }
    }
    const attachments = allAttachments.filter((a) => !inlineResolvedIds.has(a.attachmentId));

    return {
      id: msg.id,
      threadId: msg.threadId,
      subject: h('Subject') || '(no subject)',
      from,
      to: h('To'),
      date: h('Date'),
      snippet: msg.snippet || '',
      html,
      plain,
      attachments,
      isUnread: (msg.labelIds || []).includes('UNREAD'),
      labelIds: msg.labelIds || [],
    };
  },

  async fetchGmailAttachmentBase64(messageId, attachmentId) {
    const resp = await ApiClient.get(`/Email/gmail/messages/${messageId}/attachments/${attachmentId}`);
    return toStandardBase64(resp.data?.data || '');
  },

  async fetchGmailAttachmentBlob(messageId, attachment) {
    const base64 = await this.fetchGmailAttachmentBase64(messageId, attachment.attachmentId);
    return new Blob([base64ToBytes(base64)], { type: attachment.mimeType || 'application/octet-stream' });
  },

  async fetchGmailAttachmentDataUrl(messageId, attachment) {
    const base64 = await this.fetchGmailAttachmentBase64(messageId, attachment.attachmentId);
    return `data:${attachment.mimeType || 'application/octet-stream'};base64,${base64}`;
  },

  async downloadGmailAttachment(messageId, attachment) {
    const blob = await this.fetchGmailAttachmentBlob(messageId, attachment);
    triggerBrowserDownload(blob, attachment.name);
  },

  async deleteGmailMessage(messageId) {
    await ApiClient.delete(`/Email/gmail/messages/${messageId}`);
  },

  async getGmailConnectionStatus() {
    return getGmailConnectionStatus();
  },

  getStoredZohoToken() {
    return this.getStoredProviderToken("zoho");
  },

  getStoredGmailEmail() {
    try {
      const storedAuth = JSON.parse(localStorage.getItem(EMAIL_AUTH_STORAGE_KEY));
      if (storedAuth?.emailtype === 'gmail') return storedAuth.email || '';
      const providerEntry = getStoredProviderTokens().gmail;
      return providerEntry?.email || '';
    } catch {
      return '';
    }
  },

  getStoredProviderToken(emailtype) {
    const providerTokens = getStoredProviderTokens();
    const providerToken = providerTokens?.[emailtype]?.access_token;
    if (providerToken) return providerToken;

    try {
      const storedAuth = JSON.parse(localStorage.getItem(EMAIL_AUTH_STORAGE_KEY));
      return storedAuth?.emailtype === emailtype ? storedAuth.access_token : null;
    } catch {
      return null;
    }
  },

  setStoredEmailAuth(emailtype, accessToken, email = '') {
    const providerTokens = getStoredProviderTokens();
    providerTokens[emailtype] = { emailtype, access_token: accessToken, email };
    setStoredProviderTokens(providerTokens);

    localStorage.setItem(
      EMAIL_AUTH_STORAGE_KEY,
      JSON.stringify({ emailtype, access_token: accessToken, email }),
    );
    persistActiveEmailProvider(emailtype, { fromEmail: email, accessToken });
  },

  setActiveEmailProvider(emailType, meta = {}) {
    persistActiveEmailProvider(emailType, meta);
  },

  setActiveSmtpProvider(fromEmail, configId = '') {
    persistActiveEmailProvider('smtp', {
      fromEmail: fromEmail || '',
      configId,
      accessToken: '',
    });
  },

  getStoredEmailAuthPayload() {
    try {
      return resolveStoredEmailAuthPayload();
    } catch {
      return null;
    }
  },

  getCalendarEmailAuthPayload() {
    const resolved = resolveStoredEmailAuthPayload();
    if (!resolved) {
      return toEmailAuthPayload('', '', '');
    }

    const emailType = (resolved.emailType || '').toLowerCase();

    if (emailType === 'gmail') {
      // Gmail's token is resolved server-side now — no accessToken to send at all.
      return toEmailAuthPayload(
        'gmail',
        '',
        this.getStoredGmailEmail() || resolved.fromEmail || '',
      );
    }

    if (emailType === 'zoho') {
      return toEmailAuthPayload(
        'zoho',
        this.getStoredZohoToken() || resolved.accessToken || '',
        resolved.fromEmail || '',
      );
    }

    if (emailType === 'smtp') {
      return buildSmtpAuthPayload() || resolved;
    }

    return resolved;
  },

  async ensureCalendarEmailAuthPayload() {
    const payload = this.getCalendarEmailAuthPayload();

    if (payload.emailType !== 'gmail') {
      return payload;
    }

    const status = await this.getGmailConnectionStatus().catch(() => null);
    if (!status?.connected) {
      throw new Error(GMAIL_SESSION_EXPIRED_MESSAGE);
    }

    if (!payload.fromEmail) {
      try {
        const userInfo = await this.getGoogleUserInfo();
        const email = userInfo.data?.email || '';
        if (email) {
          this.storeGmailAuth(email);
          payload.fromEmail = email;
        }
      } catch {
        // Keep going; backend may still accept the request.
      }
    }

    return payload;
  },

  storeGmailAuth(email = '') {
    this.setStoredEmailAuth('gmail', '', email);
  },

  storeZohoAuth(access_token, email = '') {
    this.setStoredEmailAuth('zoho', access_token, email);
  },

  async clearStoredEmailAuth(emailtype) {
    try {
      if (!emailtype) {
        localStorage.removeItem(EMAIL_AUTH_STORAGE_KEY);
        localStorage.removeItem(EMAIL_AUTH_PROVIDERS_STORAGE_KEY);
        return;
      }

      if (emailtype === 'gmail') {
        await disconnectGmail().catch(() => {});
      }

      const providerTokens = getStoredProviderTokens();
      delete providerTokens[emailtype];
      setStoredProviderTokens(providerTokens);

      const storedAuth = JSON.parse(localStorage.getItem(EMAIL_AUTH_STORAGE_KEY));
      if (storedAuth?.emailtype === emailtype) {
        localStorage.removeItem(EMAIL_AUTH_STORAGE_KEY);
      }

      const active = getActiveEmailProvider();
      if (active?.emailType === emailtype) {
        localStorage.removeItem(EMAIL_AUTH_ACTIVE_KEY);
      }
    } catch {}
  },

  async getZohoMessages(accessToken, folder, limit = 20) {
    if (!accessToken) throw new Error('Missing Zoho access token');
    const resp = await ApiClient.post('/Email/zoho-messages', { accessToken, folder, limit });
    return (resp.data?.data || []).map((m) => ({
      id: m.messageId,
      folderId: m.folderId,
      subject: m.subject || '(no subject)',
      from: parseZohoSender(m.fromAddress, m.sender || ''),
      date: m.receivedTime ? new Date(Number(m.receivedTime)).toISOString() : '',
      snippet: m.summary || '',
      isUnread: !m.isRead,
      labelIds: [],
    }));
  },

  async getZohoAttachmentInfo(accessToken, messageId, folderId) {
    if (!accessToken) throw new Error('Missing Zoho access token');
    try {
      const resp = await ApiClient.post('/Email/zoho-attachment-info', { accessToken, messageId, folderId });
      const d = resp.data?.data || {};
      const regular = (d.attachments || []).map((a) => ({
        attachmentId: a.attachmentId,
        name: a.attachmentName || 'attachment',
        size: Number(a.attachmentSize || 0),
        mimeType: guessMimeTypeFromName(a.attachmentName || ''),
        contentId: null,
        isInline: false,
        folderId,
      }));
      const inline = (d.inline || []).map((a) => ({
        attachmentId: a.attachmentId,
        name: a.attachmentName || 'attachment',
        size: Number(a.attachmentSize || 0),
        mimeType: guessMimeTypeFromName(a.attachmentName || ''),
        contentId: a.cid || null,
        isInline: true,
        folderId,
      }));
      return [...regular, ...inline];
    } catch (err) {
      console.warn('[Zoho attachment-info] request failed', err?.response?.data || err);
      return [];
    }
  },

  async fetchZohoInlineImageDataUrl(accessToken, messageId, folderId, contentId, fileName) {
    const resp = await ApiClient.post('/Email/zoho-inline-image', {
      accessToken, messageId, folderId, contentId, fileName,
    }, { responseType: 'blob' });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(resp.data);
    });
  },

  async getZohoMessage(accessToken, messageId, folderId) {
    if (!accessToken) throw new Error('Missing Zoho access token');
    const resp = await ApiClient.post('/Email/zoho-message', { accessToken, messageId, folderId });
    const d = resp.data?.data || {};
    const fromName = d.fromDisplayName || d.sender || d.fromAddress || '';
    const fromEmail = d.fromAddress || '';
    const rawDate = d.sentDateInGMT || d.receivedTime || '';
    const date = rawDate
      ? (isNaN(Number(rawDate)) ? rawDate : new Date(Number(rawDate)).toISOString())
      : '';
    const isHtml = /<[a-z][\s\S]*>/i.test(d.content || '');
    let html = isHtml ? (d.content || '') : '';
    const plain = isHtml ? '' : (d.content || '');

    const allAttachments = await this.getZohoAttachmentInfo(accessToken, messageId, folderId);

    if (html) {
      // Zoho rewrites inline images (including signature images, which aren't real
      // message attachments) to its own webmail proxy URL rather than a `cid:` reference.
      // The proxy URL carries the same `cid` Zoho's dedicated inline-image API expects.
      const imgSrcMatches = [...html.matchAll(/<img[^>]*\ssrc=["'](\/mail\/ImageDisplay\?[^"']+)["']/gi)];
      for (const match of imgSrcMatches) {
        const rawSrc = match[1];
        const decoded = rawSrc.replace(/&amp;/g, '&');
        const params = new URLSearchParams(decoded.split('?')[1] || '');
        const contentId = params.get('cid');
        const fileName = params.get('f');
        if (!contentId) continue;
        try {
          const dataUrl = await this.fetchZohoInlineImageDataUrl(accessToken, messageId, folderId, contentId, fileName);
          html = html.split(rawSrc).join(dataUrl);
        } catch (err) {
          console.warn('[Zoho inline] failed to fetch inline image', rawSrc, err);
        }
      }
    }
    const attachments = allAttachments;

    return {
      id: messageId,
      subject: d.subject || '(no subject)',
      from: { name: fromName, email: fromEmail },
      to: d.toAddress || '',
      date,
      html,
      plain,
      snippet: d.summary || '',
      attachments,
      isUnread: !d.isRead,
      labelIds: [],
    };
  },

  async fetchZohoAttachmentBlob(accessToken, messageId, attachment) {
    if (!accessToken) throw new Error('Missing Zoho access token');
    try {
      const resp = await ApiClient.post('/Email/zoho-attachment', {
        accessToken,
        messageId,
        folderId: attachment.folderId,
        attachmentId: attachment.attachmentId,
        fileName: attachment.name,
      }, { responseType: 'blob' });
      return resp.data;
    } catch (err) {
      const errBlob = err?.response?.data;
      if (errBlob instanceof Blob) {
        console.warn('[Zoho attachment] download failed', attachment, await errBlob.text());
      } else {
        console.warn('[Zoho attachment] download failed', attachment, err);
      }
      throw err;
    }
  },

  async downloadZohoAttachment(accessToken, messageId, attachment) {
    const blob = await this.fetchZohoAttachmentBlob(accessToken, messageId, attachment);
    triggerBrowserDownload(blob, attachment.name);
  },

  async deleteZohoMessage(accessToken, messageId, folderId) {
    if (!accessToken) throw new Error('Missing Zoho access token');
    await ApiClient.delete('/Email/zoho-message', { data: { accessToken, messageId, folderId } });
  },

  async isEmailConfigured() {
    const gmailStatus = await this.getGmailConnectionStatus().catch(() => null);
    if (gmailStatus?.connected) return true;
    if (this.getStoredZohoToken()) return true;

    try {
      const smtpSessions = JSON.parse(localStorage.getItem('smtp_sessions') || '[]');
      if (Array.isArray(smtpSessions) && smtpSessions.length > 0) return true;
    } catch {
      // ignore parse errors
    }

    return SmtpConfigService.getConfigs().some((config) => Boolean(config.senderEmail));
  },

  isGmailSessionExpiredError(err) {
    return isGmailSessionExpiredError(err);
  },

  async shouldShowCalendarEmailBanner() {
    if (!(await this.isEmailConfigured())) return true;

    try {
      await this.ensureCalendarEmailAuthPayload();
      return false;
    } catch (err) {
      if (isGmailSessionExpiredError(err)) return true;
      throw err;
    }
  },
};

export default EmailService;
